/**
 * Declarative field layout for the variant form (PTV2-1172), mirroring the
 * "Variant Result Fields — FINAL" spec: a shared Core group plus one group
 * per variant type, shown only when `variantType` matches `visibleFor`.
 *
 * Each field's `key` is the draft/DTO field name; `VariantFormDialog` renders
 * the groups generically. `ref_allele` / `alt_allele` intentionally recur in
 * the SNV, Indel, and mtDNA groups — only one variant-type group is visible at
 * a time, so they never render twice.
 */

import {
  CHROMOSOME_OPTIONS,
  CNV_CLASSIFICATION_OPTIONS,
  CNV_FUNCTIONAL_IMPACT_OPTIONS,
  CNV_STRUCTURAL_IMPACT_OPTIONS,
  CNV_TYPE_OPTIONS,
  EFFECT_OPTIONS,
  EVIDENCE_OPTIONS,
  INHERITANCE_OPTIONS,
  INTERPRETATION_OPTIONS,
  REFERENCE_GENOME_OPTIONS,
  SANGER_VALIDATION_OPTIONS,
  SEGREGATION_OPTIONS,
  STATUS_OPTIONS,
  STRATEGY_OPTIONS,
  STRUCTURAL_TYPE_OPTIONS,
  VARIANT_TYPE_OPTIONS,
  ZYGOSITY_MTDNA_OPTIONS,
  ZYGOSITY_SNV_OPTIONS,
  type FieldOption,
} from '../../data/geneticFieldOptions';

export type FieldKind =
  | 'gene'
  | 'text'
  | 'number'
  | 'select'
  | 'multiselect'
  | 'taglist'
  | 'date'
  | 'textarea';

export interface FieldSpec {
  /** Draft key / DTO field name. */
  readonly key: string;
  /** i18n key under `patient.geneticInfo.fields.*`. */
  readonly labelKey: string;
  readonly kind: FieldKind;
  readonly options?: readonly FieldOption[];
  /** Render across the whole grid row (multi-selects, textarea, gene). */
  readonly full?: boolean;
  /** Show a red `*` on the label (PTV2-1301). */
  readonly required?: boolean;
}

export interface FieldGroup {
  /** i18n key under `patient.geneticInfo.groups.*`; `core` has no heading. */
  readonly id: string;
  /** Variant-type wire values this group applies to; omit = always shown. */
  readonly visibleFor?: readonly string[];
  readonly fields: readonly FieldSpec[];
}

const f = (
  key: string,
  labelKey: string,
  kind: FieldKind,
  extra: Partial<FieldSpec> = {},
): FieldSpec => ({ key, labelKey, kind, ...extra });

const L = 'patient.geneticInfo.fields';

const CORE: FieldGroup = {
  id: 'core',
  fields: [
    f('gene', `${L}.gene`, 'gene', { required: true }),
    f('variantType', `${L}.variantType`, 'select', { options: VARIANT_TYPE_OPTIONS }),
    f('referenceGenome', `${L}.referenceGenome`, 'select', {
      options: REFERENCE_GENOME_OPTIONS,
    }),
    f('chromosome', `${L}.chromosome`, 'select', { options: CHROMOSOME_OPTIONS }),
    f('start', `${L}.start`, 'text'),
    f('end', `${L}.end`, 'text'),
    f('transcript', `${L}.transcript`, 'text'),
    f('protein', `${L}.protein`, 'text'),
    f('cdna', `${L}.cdna`, 'text'),
    f('interpretation', `${L}.interpretation`, 'select', {
      options: INTERPRETATION_OPTIONS,
    }),
    f('status', `${L}.status`, 'select', { options: STATUS_OPTIONS }),
    f('inheritance', `${L}.inheritance`, 'select', { options: INHERITANCE_OPTIONS }),
    f('effects', `${L}.effect`, 'multiselect', { options: EFFECT_OPTIONS, full: true }),
    f('strategies', `${L}.strategy`, 'multiselect', {
      options: STRATEGY_OPTIONS,
      full: true,
    }),
    f('evidences', `${L}.evidence`, 'multiselect', {
      options: EVIDENCE_OPTIONS,
      full: true,
    }),
    f('segregation', `${L}.segregation`, 'select', { options: SEGREGATION_OPTIONS }),
    f('sangerValidation', `${L}.sangerValidation`, 'select', {
      options: SANGER_VALIDATION_OPTIONS,
    }),
    f('dbsnp', `${L}.dbsnp`, 'text'),
    f('dateObserved', `${L}.dateObserved`, 'date'),
    f('labName', `${L}.labName`, 'text'),
    f('comments', `${L}.comments`, 'textarea', { full: true }),
  ],
};

const SNV: FieldGroup = {
  id: 'snv',
  visibleFor: ['snv'],
  fields: [
    f('refAllele', `${L}.refAllele`, 'text'),
    f('altAllele', `${L}.altAllele`, 'text'),
    f('zygositySnv', `${L}.zygosity`, 'select', { options: ZYGOSITY_SNV_OPTIONS }),
  ],
};

const INDEL: FieldGroup = {
  id: 'indel',
  visibleFor: ['indel'],
  fields: [
    f('refAllele', `${L}.refAllele`, 'text'),
    f('altAllele', `${L}.altAllele`, 'text'),
  ],
};

const CNV: FieldGroup = {
  id: 'cnv',
  visibleFor: ['cnv'],
  fields: [
    f('cnvType', `${L}.cnvType`, 'select', { options: CNV_TYPE_OPTIONS }),
    f('cnvClassification', `${L}.cnvClassification`, 'select', {
      options: CNV_CLASSIFICATION_OPTIONS,
    }),
    f('cnvStructuralImpact', `${L}.cnvStructuralImpact`, 'select', {
      options: CNV_STRUCTURAL_IMPACT_OPTIONS,
    }),
    f('cnvFunctionalImpact', `${L}.cnvFunctionalImpact`, 'select', {
      options: CNV_FUNCTIONAL_IMPACT_OPTIONS,
    }),
    f('cytoband', `${L}.cytoband`, 'text'),
    f('copyNumber', `${L}.copyNumber`, 'number'),
  ],
};

const MTDNA: FieldGroup = {
  id: 'mtdna',
  visibleFor: ['mitochondrial'],
  fields: [
    f('refAllele', `${L}.refAllele`, 'text'),
    f('altAllele', `${L}.altAllele`, 'text'),
    f('mtdna', `${L}.mtdna`, 'text'),
    f('zygosityMtdna', `${L}.zygosity`, 'select', { options: ZYGOSITY_MTDNA_OPTIONS }),
    f('heteroplasmyPercent', `${L}.heteroplasmyPercent`, 'number'),
  ],
};

const STRUCTURAL: FieldGroup = {
  id: 'structural',
  visibleFor: ['structural'],
  fields: [
    f('structuralType', `${L}.structuralType`, 'select', {
      options: STRUCTURAL_TYPE_OPTIONS,
    }),
    f('breakpoints', `${L}.breakpoints`, 'taglist', { full: true }),
    f('chromosomeBreakpoint', `${L}.chromosomeBreakpoint`, 'text'),
    f('positionBreakpoint', `${L}.positionBreakpoint`, 'text'),
    f('fusion', `${L}.fusion`, 'text'),
  ],
};

const REPEAT: FieldGroup = {
  id: 'repeat',
  visibleFor: ['repeat_expansion'],
  fields: [
    f('repeatUnit', `${L}.repeatUnit`, 'text'),
    f('repeatCount', `${L}.repeatCount`, 'number'),
  ],
};

export const VARIANT_FIELD_GROUPS: readonly FieldGroup[] = [
  CORE,
  SNV,
  INDEL,
  CNV,
  MTDNA,
  STRUCTURAL,
  REPEAT,
];

/**
 * Flat, ordered field list for the given variant type: the Core fields with
 * the matching type-specific group spliced in immediately after `variantType`,
 * so the extra fields surface right where the user changed the type rather
 * than at the bottom of the form.
 */
export function orderedVariantFields(variantType: string): readonly FieldSpec[] {
  const typeGroup = VARIANT_FIELD_GROUPS.find(
    (g) => g.visibleFor?.includes(variantType) ?? false,
  );
  const out: FieldSpec[] = [];
  for (const field of CORE.fields) {
    out.push(field);
    if (field.key === 'variantType' && typeGroup) out.push(...typeGroup.fields);
  }
  return out;
}
